import {
  Coffee,
  Code2,
  Dices,
  Feather,
  GraduationCap,
  type LucideIcon,
} from 'lucide-react';

export type StarterPrompt = {
  /** Shown in the expanded panel + dropdown row. */
  label: string;
  /**
   * Text written into the composer on click. The "Hi!" opener +
   * the "ask me 1-2 key questions" hint match the elicit-missing-
   * context-first prompt-style. Empty string clears the textarea (for
   * the "Just chatting" surprise-me prompt — model picks the
   * direction).
   */
  prefill: string;
};

export type StarterCategory = {
  /** Stable id used for the active-expansion state. */
  id: string;
  /** Chip + dropdown-submenu icon. */
  icon: LucideIcon;
  /** Chip label + dropdown-submenu trigger label. */
  label: string;
  /** 4-5 prompts per category. */
  prompts: StarterPrompt[];
};

export const STARTER_CATEGORIES: StarterCategory[] = [
  {
    id: 'learn',
    icon: GraduationCap,
    label: 'Learn',
    prompts: [
      {
        label: 'Create assessment questions',
        prefill:
          "Hi! Could you create a set of assessment questions for a topic I'm studying? If you need more information from me, ask me 1-2 key questions right away.",
      },
      {
        label: 'Develop learning objectives',
        prefill:
          'Hi! Could you develop learning objectives? If you need more information from me, ask me 1-2 key questions right away.',
      },
      {
        label: 'Design learning challenges',
        prefill:
          'Hi! Could you design a few learning challenges to practice a concept? Ask me 1-2 questions to anchor the difficulty and topic.',
      },
      {
        label: 'Develop concept maps',
        prefill:
          'Hi! Could you help me develop a concept map for a topic? Ask me 1-2 questions about the scope and audience first.',
      },
      {
        label: 'Prepare for an exam or interview',
        prefill:
          'Hi! Could you help me prepare for an exam or interview? Ask me 1-2 questions about the format and topic first.',
      },
    ],
  },
  {
    id: 'write',
    icon: Feather,
    label: 'Write',
    prompts: [
      {
        label: 'Draft an email',
        prefill:
          'Hi! Could you help me draft an email? Tell me 1-2 things you need to know about the recipient and what I want to say.',
      },
      {
        label: 'Outline an essay or article',
        prefill:
          'Hi! Could you outline an essay or article? Ask me 1-2 questions about the topic and angle first.',
      },
      {
        label: 'Write a blog post',
        prefill:
          'Hi! Could you help me write a blog post? Ask me 1-2 questions about the topic, audience, and tone.',
      },
      {
        label: 'Polish my draft',
        prefill:
          "Hi! I'll paste a draft next — could you polish it for clarity and flow without changing my voice?\n\n",
      },
      {
        label: 'Brainstorm story ideas',
        prefill:
          'Hi! Could you brainstorm a handful of story ideas? Ask me 1-2 questions about genre and constraints first.',
      },
    ],
  },
  {
    id: 'code',
    icon: Code2,
    label: 'Code',
    prompts: [
      {
        label: 'Explain this code',
        prefill:
          "Hi! I'll paste some code next — could you explain what it does and any gotchas?\n\n```\n\n```",
      },
      {
        label: 'Debug an error',
        prefill:
          "Hi! I'm hitting an error — could you help me debug it? Ask me 1-2 questions if context is missing. The error is:\n\n```\n\n```",
      },
      {
        label: 'Write tests',
        prefill:
          "Hi! Could you write tests for some code I'll paste? Ask me 1-2 questions about the test framework and coverage goal.\n\n```\n\n```",
      },
      {
        label: 'Refactor for clarity',
        prefill:
          'Hi! Could you refactor the code I paste below for clarity? Keep the behaviour identical and call out any risks.\n\n```\n\n```',
      },
      {
        label: 'Compare two approaches',
        prefill:
          "Hi! Could you compare two approaches I'm considering? Ask me 1-2 questions about the constraints first.",
      },
    ],
  },
  {
    id: 'life',
    icon: Coffee,
    label: 'Life stuff',
    prompts: [
      {
        label: 'Plan a trip',
        prefill:
          "Hi! Could you help me plan a trip? Ask me 1-2 questions about destination, dates, and what I'm hoping to do.",
      },
      {
        label: 'Suggest recipes',
        prefill:
          "Hi! Could you suggest some recipes? Ask me 1-2 questions about what I have, dietary needs, and how much time I've got.",
      },
      {
        label: 'Make a workout plan',
        prefill:
          'Hi! Could you put together a workout plan? Ask me 1-2 questions about my goals, fitness level, and available equipment.',
      },
      {
        label: 'Help me decide',
        prefill:
          'Hi! Could you help me think through a decision? Ask me 1-2 questions about the options and what matters most to me.',
      },
      {
        label: 'Practice a tough conversation',
        prefill:
          "Hi! Could you help me practice a conversation I'm nervous about? Ask me 1-2 questions about who I'm talking to and what I want to come away with.",
      },
    ],
  },
  {
    id: 'surprise',
    icon: Dices,
    label: 'Just chatting',
    prompts: [
      {
        label: 'Surprise me',
        prefill: '',
      },
      {
        label: 'Recommend something to read',
        prefill:
          'Hi! Could you recommend something interesting to read? Ask me 1-2 questions about my recent interests.',
      },
      {
        label: 'Teach me something new',
        prefill:
          "Hi! Teach me something new I probably don't know. Pick something curious and explain it well.",
      },
      {
        label: 'Ask me a thought-provoking question',
        prefill:
          "Hi! Ask me a thought-provoking question and let's talk through it.",
      },
    ],
  },
];
